import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSprint } from "@/lib/music/useSprint";
import { META_V, TAGS, genreList, readCover, readMeta, withMeta } from "@/lib/music/meta";
import { isMp3, writeMp3 } from "@/lib/music/tagWriter";
import { canRemember, fileFromHandle, fileHandleFor, folderArt, kv, scanFiles, scanHandle, shuffled } from "@/lib/music/store";

const Ctx = createContext(null);
export const useMusic = () => useContext(Ctx);
export const BUILTIN_MOODS = ["Calm", "Melancholy", "Romantic", "Tense", "Epic", "Mysterious", "Dark", "Joyful", "Whimsical", "Adventurous"];
export const FAV = "favorites";
const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const usePersisted = (key, init) => {
  const [v, setV] = useState(init);
  const [ready, setReady] = useState(false);
  const loaded = useRef(false);
  useEffect(() => {
    const done = () => { loaded.current = true; setReady(true); };
    kv.get(key).then((x) => { if (x !== undefined) setV(x); done(); }).catch(done);
  }, [key]);
  useEffect(() => { if (loaded.current) kv.set(key, v).catch(() => {}); }, [key, v]);
  return [v, setV, ready];
};

const useLibrary = () => {
  const [status, setStatus] = useState("none");
  const [tracks, setTracks] = useState([]);
  const [dirName, setDirName] = useState("");
  const dir = useRef(null);
  const session = useRef(null);
  const [checking, setChecking] = useState(false);
  const known = useRef(null);
  const scan = useCallback(async (h, mode = "full") => {
    const quiet = mode !== "full";
    if (quiet) setChecking(true); else setStatus("scanning");
    try {
      const t = await scanHandle(h);
      const fresh = known.current ? t.filter((x) => !known.current.has(x.path)).length : 0;
      known.current = new Set(t.map((x) => x.path));
      setTracks(t); await kv.set("tracks", t); setStatus("ready");
      if (!t.length) toast.info("No music files found in that folder");
      else if (fresh) toast.success(`Found ${fresh.toLocaleString()} new ${fresh === 1 ? "song" : "songs"} in “${h.name}”`);
      else if (mode === "manual") toast.success("Your music is up to date");
    } catch {
      if (mode === "full") { setStatus("needs-permission"); toast.error("Couldn't read that folder"); }
      if (mode === "manual") toast.error(`Couldn't read “${h.name}”. It may have moved; choose the folder again.`);
    }
    setChecking(false);
  }, []);
  useEffect(() => {
    (async () => {
      const h = await kv.get("dir").catch(() => null);
      if (!h) return;
      dir.current = h; setDirName(h.name);
      const cached = (await kv.get("tracks")) || [];
      known.current = new Set(cached.map((x) => x.path));
      setTracks(cached);
      const ok = (await h.queryPermission({ mode: "read" })) === "granted";
      setStatus(ok ? "ready" : "needs-permission");
      if (ok) scan(h, "auto");
    })();
  }, []);
  const connect = async () => {
    try { const h = await window.showDirectoryPicker({ id: "lf-music", mode: "read" }); dir.current = h; session.current = null; known.current = null; setDirName(h.name); await kv.set("dir", h); await scan(h); }
    catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open that folder"); }
  };
  const reconnect = async () => { if (dir.current && (await dir.current.requestPermission({ mode: "read" })) === "granted") { setStatus("ready"); scan(dir.current, "auto"); } };
  const rescan = () => dir.current && scan(dir.current, "manual");
  const pickFiles = (files) => { const r = scanFiles(files); session.current = r.map; dir.current = null; setTracks(r.tracks); setDirName("This session"); setStatus("session"); };
  const forget = async () => { await kv.del("dir"); await kv.del("tracks"); dir.current = null; session.current = null; setTracks([]); setDirName(""); setStatus("none"); };
  const art = async (path) => (dir.current ? folderArt(dir.current, path).catch(() => null) : null);
  const file = async (path) => (session.current ? session.current.get(path) : fileFromHandle(dir.current, path));
  const canWrite = !!dir.current && !session.current && typeof FileSystemFileHandle !== "undefined" && "createWritable" in FileSystemFileHandle.prototype;
  const writeAccess = async () => (await dir.current.requestPermission({ mode: "readwrite" })) === "granted";
  return { status, tracks, dirName, connect, reconnect, rescan, pickFiles, forget, file, art, canRemember, checking, canWrite, writeAccess, handleFor: (path) => fileHandleFor(dir.current, path) };
};

export const MusicProvider = ({ children }) => {
  const lib = useLibrary();
  const audio = useRef(typeof Audio !== "undefined" ? new Audio() : null);
  const url = useRef(null);
  const [playlists, setPlaylists, playlistsReady] = usePersisted("playlists", []);
  const [meta, setMeta, metaReady] = usePersisted("meta", {});
  const [customMoods, setCustomMoods] = usePersisted("customMoods", []);
  const [metaProgress, setMetaProgress] = useState(null);
  const [storyLists, setStoryLists, listsReady] = usePersisted("storyLists", {});
  const [moods, setMoods] = usePersisted("moods", {});
  const [prefs, setPrefs] = usePersisted("prefs", { shuffle: false, repeat: "off", volume: 0.8 });
  const [sprintHistory, setSprintHistory] = usePersisted("sprints", []);
  const sprint = useSprint({ audio, volume: prefs.volume, history: sprintHistory, setHistory: setSprintHistory });
  const [q, setQ] = useState({ paths: [], order: [], pos: 0, label: "", key: null });
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [cover, setCover] = useState(null);
  const [edits, setEdits] = usePersisted("edits", {});
  const tracks = useMemo(() => lib.tracks.map((t) => withMeta(withMeta(t, meta[t.path]), edits[t.path])), [lib.tracks, meta, edits]);
  const byPath = useMemo(() => new Map(tracks.map((t) => [t.path, t])), [tracks]);
  const allMoods = useMemo(() => [...BUILTIN_MOODS, ...customMoods], [customMoods]);
  const genres = useMemo(() => genreList(tracks), [tracks]);

  useEffect(() => { if (playlistsReady && !playlists.some((p) => p.id === FAV)) setPlaylists((l) => [{ id: FAV, name: "Favorites", tracks: [] }, ...l]); }, [playlistsReady, playlists, setPlaylists]);

  const metaRef = useRef(meta);
  const retried = useRef(new Set());
  metaRef.current = meta;
  useEffect(() => {
    if (!metaReady || !["ready", "session"].includes(lib.status) || !lib.tracks.length) return undefined;
    let live = true;
    (async () => {
      await new Promise((r) => setTimeout(r, 800));
      const todo = lib.tracks.filter((t) => { const x = metaRef.current[t.path]; return !x || (x.failed ? !retried.current.has(t.path) : (x.v || 0) < META_V); });
      todo.forEach((t) => retried.current.add(t.path));
      if (!todo.length || !live) return;
      let batch = {};
      for (let i = 0; i < todo.length && live; i += 1) {
        try { batch[todo[i].path] = await readMeta(await lib.file(todo[i].path)); } catch { batch[todo[i].path] = { failed: true }; }
        if ((i + 1) % 40 === 0 || i === todo.length - 1) {
          const b = batch; batch = {};
          setMeta((old) => ({ ...old, ...b }));
          setMetaProgress(i === todo.length - 1 ? null : { done: i + 1, total: todo.length });
        }
      }
    })();
    return () => { live = false; setMetaProgress(null); };
  }, [lib.status, lib.tracks, metaReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const currentPath = q.paths[q.order[q.pos]];
  const current = currentPath ? byPath.get(currentPath) || { path: currentPath, title: currentPath.split("/").pop(), artist: "" } : null;

  useEffect(() => { if (audio.current) audio.current.volume = prefs.volume; }, [prefs.volume]);
  const loadCover = async (f, path, alive) => {
    let blob = null;
    try { blob = await readCover(f); } catch { /* no embedded art */ }
    if (!blob) blob = await lib.art(path);
    if (!alive()) return;
    setCover((old) => { if (old) URL.revokeObjectURL(old); return blob ? URL.createObjectURL(blob) : null; });
  };
  useEffect(() => { if (!currentPath) setCover((old) => { if (old) URL.revokeObjectURL(old); return null; }); }, [currentPath]);
  useEffect(() => {
    const a = audio.current;
    if (!a || !currentPath) return undefined;
    let live = true;
    (async () => {
      try {
        const f = await lib.file(currentPath);
        if (!live || !f) return;
        if (url.current) URL.revokeObjectURL(url.current);
        url.current = URL.createObjectURL(f);
        a.volume = prefs.volume;
        a.src = url.current;
        loadCover(f, currentPath, () => live);
        await a.play();
        setBlocked(false);
      } catch (e) {
        if (!live) return;
        if (e?.name === "NotAllowedError") { setBlocked(true); return; }
        toast.error("Couldn't play that track. Try reconnecting your music folder.");
        setPlaying(false);
      }
    })();
    return () => { live = false; };
  }, [currentPath, q.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = useCallback((dir) => setQ((s) => {
    if (!s.paths.length) return s;
    let pos = s.pos + dir;
    if (pos >= s.order.length) { if (prefs.repeat === "off") { audio.current?.pause(); return s; } pos = 0; }
    if (pos < 0) pos = 0;
    return { ...s, pos, key: newId() };
  }), [prefs.repeat]);

  useEffect(() => {
    const a = audio.current;
    if (!a) return undefined;
    const on = () => setPlaying(true), off = () => setPlaying(false);
    const ended = () => { if (prefs.repeat === "one") { a.currentTime = 0; a.play(); } else step(1); };
    a.addEventListener("play", on); a.addEventListener("pause", off); a.addEventListener("ended", ended);
    return () => { a.removeEventListener("play", on); a.removeEventListener("pause", off); a.removeEventListener("ended", ended); };
  }, [prefs.repeat, step]);

  const play = (paths, start = null, label = "", meta = {}) => {
    if (!paths.length) return toast.info("Nothing to play yet");
    const first = start ? paths.indexOf(start) : prefs.shuffle ? null : 0;
    const order = prefs.shuffle ? shuffled(paths.length, first < 0 ? null : first) : [...paths.keys()];
    setQ({ paths, order, pos: prefs.shuffle ? 0 : Math.max(0, first), label, key: newId(), ...meta });
  };
  const mix = (paths, label) => { if (!paths.length) return; setQ({ paths, order: shuffled(paths.length, null), pos: 0, label, key: newId() }); };
  const toggle = () => { const a = audio.current; if (!a || !currentPath) return; if (a.paused) a.play().then(() => setBlocked(false)).catch(() => {}); else a.pause(); };
  const stop = () => { audio.current?.pause(); setQ({ paths: [], order: [], pos: 0, label: "", key: null }); setBlocked(false); };
  const setShuffle = (on) => {
    setPrefs((p) => ({ ...p, shuffle: on }));
    setQ((s) => (s.paths.length ? { ...s, order: on ? shuffled(s.paths.length, s.order[s.pos]) : [...s.paths.keys()], pos: on ? 0 : s.order[s.pos] } : s));
  };
  const cycleRepeat = () => setPrefs((p) => ({ ...p, repeat: { off: "all", all: "one", one: "off" }[p.repeat] }));

  const pl = {
    create: (name, tracks = []) => { const p = { id: newId(), name: name.trim() || "New playlist", tracks }; setPlaylists((l) => [...l, p]); return p; },
    rename: (id, name) => setPlaylists((l) => l.map((p) => (p.id === id ? { ...p, name } : p))),
    toggleFav: (path) => setPlaylists((l) => l.map((p) => (p.id === FAV ? { ...p, tracks: p.tracks.includes(path) ? p.tracks.filter((t) => t !== path) : [...p.tracks, path] } : p))),
    remove: (id) => { setPlaylists((l) => l.filter((p) => p.id !== id)); setStoryLists((m) => Object.fromEntries(Object.entries(m).filter(([, v]) => v !== id))); },
    add: (id, paths) => setPlaylists((l) => l.map((p) => (p.id === id ? { ...p, tracks: [...new Set([...p.tracks, ...paths])] } : p))),
    dropMany: (id, paths) => { const s = new Set(paths); setPlaylists((l) => l.map((p) => (p.id === id ? { ...p, tracks: p.tracks.filter((t) => !s.has(t)) } : p))); },
    drop: (id, path) => setPlaylists((l) => l.map((p) => (p.id === id ? { ...p, tracks: p.tracks.filter((t) => t !== path) } : p))),
    forStory: (sid, id) => setStoryLists((m) => { const n = { ...m }; if (id) n[sid] = id; else delete n[sid]; return n; }),
  };
  const storyOpened = (sid) => {
    const p = playlists.find((x) => x.id === storyLists[sid]);
    if (!p || !p.tracks.length || q.playlistId === p.id || !["ready", "session"].includes(lib.status)) return;
    play(p.tracks, null, p.name, { playlistId: p.id });
  };

  const favs = useMemo(() => new Set(playlists.find((p) => p.id === FAV)?.tracks || []), [playlists]);
  const moodOps = {
    set: (paths, mood, on) => setMoods((old) => {
      const n = { ...old };
      paths.forEach((p) => { const cur = n[p] || []; n[p] = on ? [...new Set([...cur, mood])] : cur.filter((x) => x !== mood); });
      return n;
    }),
    create: (name) => {
      const v = name.trim().replace(/\s+/g, " ").slice(0, 30);
      if (!v) return null;
      const hit = allMoods.find((x) => x.toLowerCase() === v.toLowerCase());
      if (hit) return hit;
      setCustomMoods((l) => [...l, v]);
      return v;
    },
    removeCustom: (name) => { setCustomMoods((l) => l.filter((x) => x !== name)); setMoods((old) => Object.fromEntries(Object.entries(old).map(([k, v]) => [k, v.filter((x) => x !== name)]))); },
  };
  const saveTags = async (paths, fields, toFile) => {
    const mp3 = toFile && lib.canWrite ? paths.filter(isMp3) : [];
    const granted = mp3.length ? await lib.writeAccess().catch(() => false) : false;
    setEdits((old) => { const n = { ...old }; paths.forEach((p) => { n[p] = { ...n[p], ...fields }; }); return n; });
    if (!mp3.length) return { saved: paths.length, written: 0, failed: 0 };
    if (!granted) { toast.error("LoreRight wasn't allowed to change your files. Your edits are saved in LoreRight only."); return { saved: paths.length, written: 0, failed: mp3.length }; }
    let written = 0, failed = 0;
    for (const p of mp3) {
      const pick = (k) => (k in fields ? fields[k] : edits[p] && k in edits[p] ? edits[p][k] : meta[p]?.[k]);
      const tags = Object.fromEntries(TAGS.map((k) => [k, pick(k)]));
      try { await writeMp3(await lib.handleFor(p), tags); written += 1; } catch { failed += 1; }
    }
    return { saved: paths.length, written, failed };
  };
  const value = { ...lib, cover, saveTags, edits, tracks, byPath, metaProgress, favs, allMoods, genres, customMoods, moodOps, audio, q, current, playing, blocked, prefs, setPrefs, play, mix, toggle, next: () => step(1), prev: () => (audio.current?.currentTime > 3 ? (audio.current.currentTime = 0) : step(-1)),
    stop, setShuffle, cycleRepeat, ...sprint, playlists, pl, storyLists, listsReady, storyOpened, moods, setMoods };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
